import { IPublisher, IUser } from '~/src/dash/api/console-api';
import { EBool } from '~/src/dash/api/types/bool';
import { DEMO_ACCOUNTS } from '~/src/dash/globals/constants';
import { isOtteraSubpublisher } from '~/src/dash/resellers/utils/importResellerConfig';
import { hasRole, ROLE_PARENT_PUBLISHER, ROLE_RESELLER } from '~/src/dash/utils/roles';
import { validPublisherType } from '~/src/dash/views/platform-connect/common';
import { PID } from '~/src/dash/utils/publishers';

export const RESELLERIDS = {
  PUBLICA: '-1',
  OTTERA: '3',
  ROKU: '4',
  EVRIDEO: '5',
  PHILO: '6',
  ORKA: '7',
  IAS: '8',
};

export function isMultiPublishersEnabled(publisher: IPublisher): boolean {
  return [
    '394', // Samsung
    '547', // Samsung Korea
    '437', // Roku
    '475', // Ottera
    '538', // Viacom CBS
  ].includes(publisher.ID);
}

export function isSamsungPublisher(publisherID: string | undefined): boolean {
  if (!publisherID) return false;
  return [
    '394', // Samsung NA
    '486', // Samsung EU
    '547', // Samsung Korea
    '642', // Samsung AU
    '643', // Samsung Brazil
    '654', // Samsung MX
    '739', // Samsung Test
    '744', // Samsung India
    '1030', // Samsung Central America
    '1208', // Samsung MENA
    '1266', // Samsung SE-Asia
  ].includes(publisherID);
}

export function isPremionPublisher(publisherID: string | undefined): boolean {
  if (!publisherID) return false;
  return [
    '773', // Tegna
    '1267', // Gateway Publica
  ].includes(publisherID);
}

// Publishers Tier 1 / Platinum
export function isTopTierPublisher(publisherID: string | undefined): boolean {
  if (!publisherID) return false;
  return (
    isPhiloPublisher(publisherID) ||
    isSamsungPublisher(publisherID) ||
    [
      '538', // Paramount
      '368', // AMC Networks - Pub
      '773', // Tegna
    ].includes(publisherID)
  );
}
export function isDemoAccount(username: string): boolean {
  return DEMO_ACCOUNTS.includes(username);
}

export function isXMLCommentWritePublisher(publisherID: string): boolean {
  return ['755', '1071'].includes(publisherID);
}

export function isSamsungNAPublisher(publisherID: string): boolean {
  return publisherID === '394';
}

export function isNinePublisher(publisherID: string): boolean {
  return publisherID === '845';
}

export function isOtteraResellerAdmin(user: IUser, publisher: IPublisher): boolean {
  return (
    hasRole(user, [ROLE_RESELLER]) &&
    publisher.Reseller &&
    publisher.Reseller.ID === RESELLERIDS.OTTERA
  );
}

export function isParentPublisherAdmin(user: IUser, publisher: IPublisher): boolean {
  return hasRole(user, [ROLE_PARENT_PUBLISHER]) && publisher.IsParentPublisher === EBool.EBool_TRUE;
}

export function isAudienceRouteDisabled(publisher: IPublisher): boolean {
  return publisher.Reseller && publisher.Reseller.ID === RESELLERIDS.ROKU;
}

export function isCampaignRouteDisabled(publisher: IPublisher): boolean {
  return publisher.Reseller && publisher.Reseller.ID === RESELLERIDS.ROKU;
}

export function isDealRouteDisabled(publisher: IPublisher): boolean {
  return (
    (publisher.Reseller && publisher.Reseller.ID === RESELLERIDS.ROKU) ||
    isOtteraSubpublisher(publisher.ID)
  );
}

export function isEndpointBuilderDisabled(publisher: IPublisher): boolean {
  return publisher.Reseller && publisher.Reseller.ID === RESELLERIDS.ROKU;
}

export function isPublicaCampaignDisabled(publisher: IPublisher): boolean {
  return publisher.Reseller && publisher.Reseller.ID === RESELLERIDS.ROKU;
}

export function isCreativeTagEventsEnabled(publisher: IPublisher): boolean {
  return (
    (publisher.Reseller && publisher.Reseller.ID === RESELLERIDS.ROKU) || publisher.ID === '39'
  ); // Publisher Newsy
}

export function isPlatformConnectDisabled(publisher: IPublisher): boolean {
  return (
    !validPublisherType.includes(publisher.PublisherType) ||
    publisher.EnablePlatformMarketplace === EBool.EBool_FALSE
  );
}

// Last 2 characters of hex color determine transparency
// see this link: https://gist.github.com/lopspower/03fb1cc0ac9f32ef38f4
// transparency should be 2 characters that match the transparency % you want from the link above
export const getTransparentColor = (color: string, transparency: string) => {
  return color + transparency;
};

export const isFirstPartyEnabled = (publisher: IPublisher) => {
  // KennyTesting has no AnId
  return (
    (publisher.EnableIASFirstPartyVASTTracking === EBool.EBool_TRUE && publisher.IASAnID) ||
    publisher.ID === '518'
  );
};

export const isOpenPathEnabled = (publisher: IPublisher) => {
  return publisher.OpenPathEnabled === EBool.EBool_TRUE;
};

export function isPhiloPublisher(publisherID: string): boolean {
  return publisherID === '381';
}

export function isTestPublisher(publisherID: string): boolean {
  return [
    '1', // Publica
    '518', // KennyTesting
    '760', // QAPublicaTesting1
    '1095', // ElsonTestingV2
  ].includes(publisherID);
}

export function isPhiloFallbackEnabledPublisher(publisherID: string): boolean {
  return isPhiloPublisher(publisherID) || isTestPublisher(publisherID);
}

export function isForecastingAllowed(publisher: IPublisher): boolean {
  if (
    publisher.IsResellerPublisher === EBool.EBool_TRUE &&
    ![PID.ottera, PID.bitCentral, PID.ruralMediaGroup, PID.fanServ].includes(publisher.ID)
  ) {
    return false;
  }
  return true;
}

export function shouldDisplayInventoryRules(publisher: IPublisher): boolean {
  return [
    PID.qaPublica,
    PID.jasonTestPublisher,
    PID.kennyTesting,
    PID.publica,
    PID.amc,
    PID.fujiTV,
    PID.sonyProd,
    PID.sonyUAT,
    PID.sonyStage,
    PID.estrellaTV,
    PID.kansaiTV,
  ].includes(publisher.ID);
}
